import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const EXPECTATION_OPTIONS = [
  'compensation',
  'redress',
  'refund',
  'contractTermination',
  'other',
] as const;

interface DescribeSituationFormProps {
  variant?: 'landing' | 'search';
}

export default function DescribeSituationForm({ variant = 'landing' }: DescribeSituationFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [when, setWhen] = useState('');
  const [expectations, setExpectations] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleExpectation = (key: string) => {
    setExpectations((prev) =>
      prev.includes(key) ? prev.filter((e) => e !== key) : [...prev, key]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setIsSubmitting(true);

    // Build search query from form fields
    let query = description.trim();
    if (when) {
      query += ` ${when}`;
    }
    const selectedLabels = expectations.map((key) =>
      t(`situationForm.expectations.${key}`)
    );
    if (selectedLabels.length > 0) {
      query += ` ${selectedLabels.join(' ')}`;
    }

    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const isLanding = variant === 'landing';

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
      <div className="space-y-5">
        {/* Main description */}
        <div>
          <label
            htmlFor="situation-description"
            className={`block font-semibold mb-2 ${
              isLanding
                ? 'text-slate-900 dark:text-white text-lg'
                : 'text-slate-700 dark:text-slate-200 text-base'
            }`}
          >
            {t('situationForm.whatHappened')}
          </label>
          <textarea
            id="situation-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('situationForm.whatHappenedPlaceholder')}
            rows={isLanding ? 5 : 4}
            className="w-full px-4 py-3 text-base border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y transition-colors"
          />
        </div>

        {/* When did it happen */}
        <div>
          <label
            htmlFor="situation-when"
            className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2"
          >
            {t('situationForm.when')}{' '}
            <span className="text-slate-400 dark:text-slate-500 font-normal">
              ({t('situationForm.optional')})
            </span>
          </label>
          <input
            id="situation-when"
            type="text"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            placeholder={t('situationForm.whenPlaceholder')}
            className="w-full px-4 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          />
        </div>

        {/* Expectations chips */}
        <div>
          <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
            {t('situationForm.whatExpect')}{' '}
            <span className="text-slate-400 dark:text-slate-500 font-normal">
              ({t('situationForm.optional')})
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            {EXPECTATION_OPTIONS.map((key) => {
              const selected = expectations.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleExpectation(key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selected
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {t(`situationForm.expectations.${key}`)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!description.trim() || isSubmitting}
          className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed transition-colors text-base"
        >
          {isSubmitting ? t('situationForm.searching') : t('situationForm.submit')}
        </button>
      </div>
    </form>
  );
}
